import React, { useState } from 'react';
import { ArrowLeft, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      alert('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API_BASE}/api/auth/forgot-password`, {
        email: email.toLowerCase().trim()
      });
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Forgot password request failed:', error);
      // Still show success message for security (don't reveal if email exists)
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-md mx-auto px-6 py-8">
        <Link to="/login">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Sign In
          </Button>
        </Link>

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            {isSubmitted ? (
              <CheckCircle size={40} className="text-green-400" />
            ) : (
              <Mail size={40} className="text-turquoise-400" />
            )}
          </div>
          <h1 className="display-large mb-4">
            {isSubmitted ? (
              <>Check Your <span className="turquoise-text-gradient">Email</span></>
            ) : (
              <>Forgot Your <span className="turquoise-text-gradient">Password?</span></>
            )}
          </h1>
          <p className="body-large text-gray-300">
            {isSubmitted ? (
              "We've sent a password reset link to your email address if it's registered with us."
            ) : (
              "No worries! Enter your email address and we'll send you a reset link."
            )}
          </p>
        </div>

        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 mb-6">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Mail size={16} className="inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                  placeholder="Enter your registered email"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="btn-primary-enhanced w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sending Reset Link...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send Reset Link
                </>
              )}
            </Button>
          </form>
        ) : (
          <div className="bg-green-500/10 border border-green-500/20 rounded-16 p-6 mb-6">
            <div className="text-center">
              <h3 className="text-green-400 font-medium mb-3">Reset Link Sent!</h3>
              <p className="text-gray-300 text-sm mb-4">
                If your email is registered with us, you'll receive a password reset link within the next few minutes.
              </p>
              <p className="text-gray-400 text-xs mb-4">
                Check your spam/junk folder if you don't see it in your inbox.
              </p>
              <Link to="/login">
                <Button className="btn-secondary w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="text-center">
          <p className="text-gray-400 mb-4">Remember your password?</p>
          <Link to="/login">
            <Button className="btn-secondary w-full">
              Sign In Instead
            </Button>
          </Link>
        </div>

        <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h3 className="text-blue-400 font-medium mb-2">🔐 Security Note</h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>• Reset links expire after 1 hour</li>
            <li>• Only one reset link is active at a time</li>
            <li>• We never send passwords via email</li>
            <li>• Contact support if you need additional help</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;